import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import type { GetDraftProviderOptionsResponseType } from "api/response";
import { quickRequestQueryKeys } from "api/queries/quick-request/quickRequestQueryKeys";
import { convertDraftToRequest } from "@RequestFormV2/api/draftApi";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { useRequestFormSave } from "@RequestFormV2/context/save";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { mapProviderOptions } from "@RequestFormV2/sections/Provider/utils/mapProviderOptions";
import errorToast from "helpers/errorToast";
import { resolveServerErrors } from "helpers/requestFormHelper";
import {
  flattenApiErrors,
  countPackingListRows,
  trackQuoteFormErrorLogged,
  trackQuoteFormSubmitted,
} from "@RequestFormV2/formEvents/requestFormAnalytics";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";

type UseRequestFormSubmitParams = {
  isEditQuoteRequest?: boolean;
  isEditQuickRequest?: boolean;
};

export function useRequestFormSubmit({
  isEditQuoteRequest = false,
  isEditQuickRequest = false,
}: UseRequestFormSubmitParams = {}) {
  const mode = useRequestFormMode();
  const draftId = useRequestFormDraftId();
  const saveContext = useRequestFormSave();
  const { getValues, setError } = useFormContext();
  const navigate = useNavigate();
  const { shipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const quotesAvailable = searchParams.get("quotesAvailable");
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!draftId || !saveContext) return;

    setIsSubmitting(true);
    try {
      const saved = await saveContext.flushSave();
      if (!saved) {
        errorToast(undefined, "Unable to save draft. Please try again.");
        return;
      }
      const res = await convertDraftToRequest(draftId, mode);

      if (mode === "quick") {
        const quickRequestData = res.data as { quick_request_id?: string; shipment_id: string };
        const quickRequestId = quickRequestData.quick_request_id ?? quickRequestData.shipment_id;
        if (isEditQuickRequest && shipmentId) {
          queryClient.invalidateQueries({
            queryKey: quickRequestQueryKeys.quickRequestDetails(shipmentId),
          });
        }
        navigate(`/shipment/${quickRequestId}/quick`);
        return;
      }

      const invitedProviders: string[] = getValues("invited_providers.providers") ?? [];
      const packingList = (getValues("packing_list") ?? []) as PackingListRow[];
      const packingCounts = countPackingListRows(packingList);

      trackQuoteFormSubmitted({
        providerCount: invitedProviders.length,
        totalContainers: packingCounts.total_containers,
        totalCommodities: packingCounts.total_commodities,
      });

      const providerOptionsQueries = queryClient.getQueriesData<GetDraftProviderOptionsResponseType>({
        queryKey: ["draft-provider-options", "list", draftId],
      });
      const providerOptionsData = providerOptionsQueries.find(([, data]) => data)?.[1];
      const isParcelOrRateCardAvailable =
        mapProviderOptions(providerOptionsData?.invited_providers).some((provider) => provider.rate_card_available) ||
        getValues("invited_modes")?.parcel;

      let path = `/shipment/${res.data.shipment_id}`;
      if (isParcelOrRateCardAvailable) {
        path = `/shipment/${res.data.shipment_id}?pull_quotes=true`;
      }

      // While editing rate card quotes after submitting, BE invalidates cache for quotations and we
      // request quotation at the same time causing 500 error, so redirecting with a delay.
      if (isEditQuoteRequest && shipmentId && quotesAvailable === "true") {
        setTimeout(() => {
          setIsSubmitting(false);
          navigate(path);
        }, 5000);
      } else {
        navigate(path);
      }
    } catch (err) {
      resolveServerErrors(err, { ...getValues() }, setError);
      trackQuoteFormErrorLogged(flattenApiErrors(err));
      errorToast(err);
    } finally {
      if (!isEditQuoteRequest || !shipmentId || !quotesAvailable || quotesAvailable === "false") {
        setIsSubmitting(false);
      }
    }
  }, [
    draftId,
    saveContext,
    getValues,
    navigate,
    queryClient,
    setError,
    isEditQuoteRequest,
    isEditQuickRequest,
    shipmentId,
    quotesAvailable,
    mode,
  ]);

  return { submit, isSubmitting };
}
